import axios from "axios";

// const BASE_URL = "/api";
const BASE_URL = "http://localhost/real-estate-api";

// ---------------------- AUTH ----------------------

// ✅ Login
export const login = async (username, password) => {
  if (!username || !password) {
    throw new Error("Username and password are required.");
  }

  const fd = new FormData();
  fd.append("username", username);
  fd.append("password", password);

  try {
    const res = await axios.post(`${BASE_URL}/auth/login.php`, fd, {
      withCredentials: true,
    });

    if (res?.data?.success) {
      return {
        success: true,
        user: res.data.user || null,
        role: res.data.role || null,
        forceChange: !!res.data.forceChange, // if backend signals forced password change
        message: res.data.message || "Login successful.",
      };
    }

    return {
      success: false,
      message:
        res?.data?.message || "Login failed. Please check your credentials.",
    };
  } catch (err) {
    console.error("Error during login:", err);
    return {
      success: false,
      message: "Server error while logging in.",
    };
  }
};

// ✅ Logout
export const logout = async () => {
  try {
    const res = await axios.post(
      `${BASE_URL}/auth/logout.php`,
      {},
      { withCredentials: true }
    );

    return {
      success: !!res?.data?.success,
      message:
        res?.data?.message ||
        (res?.data?.success ? "Logged out successfully." : "Logout failed."),
    };
  } catch (err) {
    console.error("Error during logout:", err);
    return {
      success: false,
      message: "Server error while logging out.",
    };
  }
};

// ✅ Check session
export const checkSession = async () => {
  try {
    const res = await axios.get(`${BASE_URL}/auth/checkSession.php`, {
      withCredentials: true,
    });

    const data = res?.data;

    if (data?.success && data?.loggedIn) {
      return {
        success: true,
        loggedIn: true,
        id: data.id,
        username: data.username || null,
        role: data.role || "user",
        forceChange: !!data.forceChange,
        message: data.message || "Session active",
      };
    }

    return {
      success: false,
      loggedIn: false,
      message: data?.message || "Not logged in.",
    };
  } catch (err) {
    console.error("Error checking session:", err);
    return {
      success: false,
      loggedIn: false,
      message: "Server error while checking session.",
    };
  }
};

// ✅ Change password
export const changePassword = async (password) => {
  if (!password) {
    throw new Error("Password is required to change.");
  }

  const fd = new FormData();
  fd.append("password", password);

  try {
    const res = await axios.post(`${BASE_URL}/auth/changePassword.php`, fd, {
      withCredentials: true,
    });

    return {
      success: !!res?.data?.success,
      message:
        res?.data?.message ||
        (res?.data?.success
          ? "Password changed successfully."
          : "Password change failed."),
    };
  } catch (err) {
    console.error("Error changing password:", err);
    return {
      success: false,
      message: "Server error while changing password.",
    };
  }
};

// ---------------------- PROPERTIES ----------------------

// ✅ Get all properties
export const getAllProperties = async () => {
  try {
    const res = await axios.get(`${BASE_URL}/properties/getAllProperties.php`, {
      withCredentials: true,
    });

    // Normalize response
    if (res?.data?.success) {
      return {
        success: true,
        properties: res.data.properties || [],
      };
    }

    return {
      success: false,
      message: res?.data?.message || "Failed to fetch properties.",
      properties: [],
    };
  } catch (err) {
    console.error("Error fetching all properties:", err);
    return {
      success: false,
      message: "Server error while fetching properties.",
      properties: [],
    };
  }
};

// ✅ Get single property by ID
export const getProperty = async (id) => {
  try {
    const res = await axios.get(
      `${BASE_URL}/properties/getProperty.php?id=${encodeURIComponent(id)}`,
      { withCredentials: true }
    );

    if (res?.data?.success) {
      return {
        success: true,
        property: res.data.property || null,
      };
    }

    return {
      success: false,
      message: res?.data?.message || "Property not found.",
      property: null,
    };
  } catch (err) {
    console.error("Error fetching property:", err);
    return {
      success: false,
      message: "Server error while fetching property.",
      property: null,
    };
  }
};

// ✅ Add property
export const addProperty = async (property) => {
  const fd = new FormData();
  fd.append("title", property.title);
  fd.append("description", property.description);
  fd.append("price", property.price);
  fd.append("location", property.location);
  fd.append("featured", property.featured ? 1 : 0);

  // If images are included
  if (property.images && property.images.length > 0) {
    property.images.forEach((file, idx) => {
      fd.append(`images[${idx}]`, file);
    });
  }

  const res = await axios.post(`${BASE_URL}/properties/addProperty.php`, fd, {
    withCredentials: true,
  });
  return res.data;
};

// ✅ Update property
export const updateProperty = async (property) => {
  const fd = new FormData();
  fd.append("id", property.id);
  fd.append("title", property.title);
  fd.append("description", property.description);
  fd.append("price", property.price);
  fd.append("location", property.location);
  fd.append("featured", property.featured ? 1 : 0);

  // If new images are included
  if (property.images && property.images.length > 0) {
    property.images.forEach((file, idx) => {
      fd.append(`images[${idx}]`, file);
    });
  }

  // If you support deleting images, you can send their IDs too
  if (property.deleteImages && property.deleteImages.length > 0) {
    property.deleteImages.forEach((id) => {
      fd.append("deleteImages[]", id);
    });
  }

  const res = await axios.post(
    `${BASE_URL}/properties/updateProperty.php`,
    fd,
    {
      withCredentials: true,
    }
  );
  return res.data;
};

// ✅ Delete property
export const deleteProperty = async (id) => {
  const fd = new FormData();
  fd.append("id", String(id)); // normalize to string

  const res = await axios.post(
    `${BASE_URL}/properties/deleteProperty.php`,
    fd,
    {
      withCredentials: true,
    }
  );

  return res.data; // backend JSON response
};

// ✅ Get property images
export const getPropertyImages = async (propertyId) => {
  if (!propertyId) {
    throw new Error("Property ID is required to fetch images.");
  }

  try {
    const res = await axios.get(
      `${BASE_URL}/properties/getPropertyImages.php?id=${encodeURIComponent(
        propertyId
      )}`,
      { withCredentials: true }
    );

    if (res?.data?.success) {
      return {
        success: true,
        images: res.data.images || [],
      };
    }

    return {
      success: false,
      message: res?.data?.message || "Failed to fetch property images.",
      images: [],
    };
  } catch (err) {
    console.error("Error fetching property images:", err);
    return {
      success: false,
      message: "Server error while fetching property images.",
      images: [],
    };
  }
};

// ✅ Upload property image
export const uploadPropertyImage = async (fd) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/properties/updatePropertyImage.php`,
      fd,
      { withCredentials: true }
    );

    return {
      success: !!res?.data?.success,
      message:
        res?.data?.message ||
        (res?.data?.success
          ? "Image uploaded successfully."
          : "Upload failed."),
    };
  } catch (err) {
    console.error("Error uploading property image:", err);
    return {
      success: false,
      message: "Server error while uploading property image.",
    };
  }
};

// ✅ Delete property image
export const deletePropertyImage = async (imageId) => {
  if (!imageId) {
    throw new Error("Image ID is required to delete a property image.");
  }

  const fd = new FormData();
  fd.append("id", String(imageId));

  try {
    const res = await axios.post(
      `${BASE_URL}/properties/deletePropertyImage.php`,
      fd,
      { withCredentials: true }
    );

    return {
      success: !!res?.data?.success,
      message:
        res?.data?.message ||
        (res?.data?.success ? "Image deleted successfully." : "Delete failed."),
    };
  } catch (err) {
    console.error("Error deleting property image:", err);
    return {
      success: false,
      message: "Server error while deleting property image.",
    };
  }
};

// ---------------------- USERS ----------------------

// ✅ Get all users
export const getAllUsers = async () => {
  try {
    const res = await axios.get(`${BASE_URL}/auth/getAllUsers.php`, {
      withCredentials: true,
    });

    if (res?.data?.success) {
      return {
        success: true,
        users: res.data.users || [],
      };
    }

    return {
      success: false,
      message: res?.data?.message || "Failed to fetch users.",
      users: [],
    };
  } catch (err) {
    console.error("Error fetching users:", err);
    return {
      success: false,
      message: "Server error while fetching users.",
      users: [],
    };
  }
};

// ✅ Add user
export const addUser = async (user) => {
  const fd = new FormData();
  fd.append("username", user.username);
  fd.append("password", user.password);
  fd.append("role", user.role);
  fd.append("name", user.name);
  fd.append("email", user.email);

  try {
    const res = await axios.post(`${BASE_URL}/auth/createUser.php`, fd, {
      withCredentials: true,
    });

    return {
      success: !!res?.data?.success,
      message:
        res?.data?.message ||
        (res?.data?.success ? "User added successfully." : "Add user failed."),
    };
  } catch (err) {
    console.error("Error adding user:", err);
    return {
      success: false,
      message: "Server error while adding user.",
    };
  }
};

// ✅ Update user
export const updateUser = async (user) => {
  const fd = new FormData();
  fd.append("id", user.id);
  fd.append("username", user.username);
  fd.append("role", user.role);
  fd.append("name", user.name);
  fd.append("email", user.email);

  try {
    const res = await axios.post(`${BASE_URL}/auth/updateUser.php`, fd, {
      withCredentials: true,
    });

    return {
      success: !!res?.data?.success,
      message:
        res?.data?.message ||
        (res?.data?.success
          ? "User updated successfully."
          : "Update user failed."),
    };
  } catch (err) {
    console.error("Error updating user:", err);
    return {
      success: false,
      message: "Server error while updating user.",
    };
  }
};

// ✅ Delete user
export const deleteUser = async (id) => {
  if (!id) throw new Error("User ID is required to delete.");

  const fd = new FormData();
  fd.append("id", String(id));

  try {
    const res = await axios.post(`${BASE_URL}/auth/deleteUser.php`, fd, {
      withCredentials: true,
    });

    return {
      success: !!res?.data?.success,
      message:
        res?.data?.message ||
        (res?.data?.success
          ? "User deleted successfully."
          : "Delete user failed."),
    };
  } catch (err) {
    console.error("Error deleting user:", err);
    return {
      success: false,
      message: "Server error while deleting user.",
    };
  }
};

// ✅ Reset user password
export const resetUserPassword = async (id, password, forceChange) => {
  if (!id || !password) {
    throw new Error("User ID and new password are required.");
  }

  const fd = new FormData();
  fd.append("id", String(id));
  fd.append("password", password);
  fd.append("force_change", forceChange ? 1 : 0);

  try {
    const res = await axios.post(`${BASE_URL}/auth/resetPassword.php`, fd, {
      withCredentials: true,
    });

    return {
      success: !!res?.data?.success,
      message:
        res?.data?.message ||
        (res?.data?.success
          ? "Password reset successfully."
          : "Password reset failed."),
    };
  } catch (err) {
    console.error("Error resetting user password:", err);
    return {
      success: false,
      message: "Server error while resetting password.",
    };
  }
};

// ---------------------- CONTACT US ----------------------

// ✅ Send contact message
export const sendContactMessage = async (formData) => {
  if (!formData || typeof formData !== "object") {
    throw new Error("Valid form data is required.");
  }

  try {
    const params = new URLSearchParams();
    Object.entries(formData).forEach(([key, value]) => {
      params.append(key, value);
    });

    const res = await axios.post(`${BASE_URL}/email/new_inquiry.php`, params, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });

    if (res?.data?.success) {
      return {
        success: true,
        message: res.data.message || "Message sent successfully.",
      };
    }

    return {
      success: false,
      message: res?.data?.message || "Failed to send message.",
    };
  } catch (err) {
    console.error("Error sending contact message:", err);
    return {
      success: false,
      message: "Server error while sending message.",
    };
  }
};
